import { WellAnalysisWidgetState } from '@go-widgets/well-logs-widget';
import type { AnalysisInitParams, AnalysisWidgetStateParams } from '@go-widgets/well-logs-widget';
import { action, makeObservable, observable, reaction } from 'mobx';

import { lagacyAgent } from 'src/api/legacyAgent';
import { wellLogsWidgetAgent } from 'src/api/wellLogsWidgetAgent';
import type { TCreateWidgetOptions, TGroup, TWellListCombinedData } from 'src/entities/tab/TabEntity';
import type { IWidgetStore } from 'src/entities/widget/IWidgetStore';
import { requireService } from 'src/packages/di';

import { setWellIdInGroup } from '../../utils/setWellIdInGroup';
import type { GroupSelectStore } from '../group-select/GroupSelect.store';

import type { WellAnalyseWidgetEntity } from './WellAnalyseWidget.entity';
import { getCorrectLocale } from './WellAnalyseWidget.utils';

export class WellAnalyseWidgetStore implements IWidgetStore {
  private readonly wellAnalyseEntity: WellAnalyseWidgetEntity;
  private readonly groupSelectStore: GroupSelectStore;
  private readonly initParams: AnalysisInitParams;

  @observable isLoading: boolean = false;
  @observable isReady: boolean = false;
  @observable hasError: boolean = false;
  @observable isGroupSelectOpen: boolean = false;

  onCreateWellAnalyseWidget: (
    wellId: number | null,
    stateParams: Partial<AnalysisWidgetStateParams> | null,
    options?: TCreateWidgetOptions,
  ) => void;

  constructor(
    wellAnalyseEntity: WellAnalyseWidgetEntity,
    groupSelectStore: GroupSelectStore,
    onCreateWellAnalyseWidget: (
      wellId: number | null,
      stateParams: Partial<AnalysisWidgetStateParams> | null,
      options?: TCreateWidgetOptions,
    ) => void,
    private readonly navigate: (wellId: number | null, wellboreId?: number | null) => void,
    private readonly wellListPanel: TWellListCombinedData,
    private readonly theme = requireService('theme'),
    private readonly otherSettings = requireService('otherSettings'),
    private readonly stompCachePublisherService = requireService('stompCachePublisherService'),
    private readonly wellStatusService = requireService('wellStatusService'),
    private readonly language = requireService('language'),
    private readonly userPreferencesService = requireService('userPreference'),
  ) {
    this.onCreateWellAnalyseWidget = onCreateWellAnalyseWidget;

    this.wellAnalyseEntity = wellAnalyseEntity;
    this.groupSelectStore = groupSelectStore;
    this.isGroupSelectOpen = wellAnalyseEntity.isGroupsDefaultOpened;

    this.initParams = {
      widgetType: 'desktop',
      legacyAxiosInstance: lagacyAgent,
      stompPublisher: this.stompCachePublisherService.stompPublisher,
      wellDirectoryServiceStompPublisher: this.wellStatusService,
      axiosInstance: wellLogsWidgetAgent,
    };

    makeObservable(this);
  }

  @action.bound
  setIsGroupSelectOpen(isOpen: boolean): void {
    this.isGroupSelectOpen = isOpen;
  }

  @action.bound
  exitFullScreen() {
    this.wellAnalyseEntity.setFullscreen(false);
  }

  @action.bound
  setGroupForWellLogs(group: TGroup | null): void {
    this.wellAnalyseEntity.setGroup(group ? group.id : null);
  }

  @action.bound
  setWellId(well: number | null): void {
    this.wellAnalyseEntity.setWellId(well);
  }

  @action.bound
  private setReady(value: boolean): void {
    this.isReady = value;
  }

  private getNewState(wellId: number): WellAnalysisWidgetState {
    return new WellAnalysisWidgetState(
      this.initParams,
      {
        // TODO: remove hardcode
        wellId,
        width: 500,
        height: 500,
        locale: getCorrectLocale(this.language.language),
        theme: this.theme.theme,
        fullScreen: this.wellAnalyseEntity.isFullscreen,
        isAltNames: this.userPreferencesService.preferences.isAltNames,
        defaultSelectWellbore: this.userPreferencesService.preferences.autoSelectWellboreForTime,
      },
      this.wellAnalyseEntity.stateParams,
      {
        onInitializedSuccess: () => {
          this.setReady(true);
        },
        onInternalStateChanged: (state) => {
          this.wellAnalyseEntity.setStateParams(state);
        },
        onTitleChanged: (value) => {
          this.wellAnalyseEntity.setCenterTitle(value);
        },
        onWellboreChanged: (wellboreName) => {
          this.wellAnalyseEntity.setWellboreName(wellboreName);
        },
        onRedirectToBasicParameters: () => {
          this.redirectToBasicParameters();
        },
      },
    );
  }

  @action.bound
  redirectToBasicParameters() {
    const wellId = this.wellAnalyseEntity.wellId;
    const wellboreId = this.wellAnalyseEntity.stateParams.wellboreId;

    this.navigate(wellId, wellboreId);
  }

  effect = () => {
    const disposeGroupId = reaction(
      () => this.wellAnalyseEntity.groupId,
      (groupId) => {
        if (groupId === null) return;

        const widgets = this.groupSelectStore.groupManager.getWidgetsByGroupId(groupId);
        setWellIdInGroup(widgets, this.wellAnalyseEntity, this.wellListPanel);
      },
    );

    const disposeWellId = reaction(
      () => this.wellAnalyseEntity.wellId,
      (wellId, prevWellId) => {
        if (wellId && !this.wellAnalyseEntity.state) {
          const newState = this.getNewState(wellId);

          this.wellAnalyseEntity.setState(newState);
        } else if (wellId && this.wellAnalyseEntity.state && typeof prevWellId !== 'undefined') {
          this.wellAnalyseEntity.state.setAppParams({ wellId });
        } else if (!wellId && this.wellAnalyseEntity.state) {
          this.wellAnalyseEntity.state.destroy();
          this.wellAnalyseEntity.setState(null);
        }
      },
      { fireImmediately: true },
    );

    const disposeStateParams = reaction(
      () => ({
        theme: this.theme.theme,
        language: this.language.language,
        isAltNames: this.userPreferencesService.preferences.isAltNames,
      }),
      ({ theme, language, isAltNames }) => {
        this.wellAnalyseEntity.state?.setAppParams({
          theme,
          locale: getCorrectLocale(language),
          isAltNames,
        });
      },
      {
        fireImmediately: true,
      },
    );

    const wellListPanelDisposer = reaction(
      () => this.wellListPanel.store.selectedWellId,
      (wellId) => {
        if (this.wellListPanel.panelState?.groupId === this.wellAnalyseEntity.groupId) {
          this.wellAnalyseEntity.setWellId(wellId);
        }
      },
      {
        fireImmediately: true,
      },
    );

    return () => {
      disposeGroupId();
      disposeWellId();
      disposeStateParams();
      wellListPanelDisposer();
    };
  };
}
